import { EventType } from "@/analytics/analytics-events";
import { AuthorizeExternalAnnotationProperties } from "@/analytics/generic-events-properties";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { selectProjectIdForIntegrationType } from "@/store/integrations/integrations-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { Tag } from "@/store/tags/tags-slice";
import {
  Alert,
  DatePicker,
  Dropdown,
  DropdownSelect,
  FaroChipTag,
  FaroRichTextEditor,
  FaroText,
  FilterMenuContent,
  grey,
  NoTranslate,
  Option,
} from "@faro-lotv/flat-ui";
import { Analytics, useTranslate } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import {
  ApiResponseError,
  BcfBackendApiErrorDetails,
  BcfServicesIntegrationType,
  GetProjectExtensionsResponse,
  IntegrationUserInfo,
  isBcfBackendApiErrorDetails,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { Grid } from "@mui/material";
import { Stack } from "@mui/system";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import {
  dropDownStyle,
  legacyGridItemRowPadding,
  legacyGridSpacing,
} from "./annotation-editor/annotation-editor-meta-fields";
import { SearchableDropdown } from "./annotation-editor/searchable-dropdown";
import {
  AccCompanionFieldId,
  AccCompanionFields,
  CommonExternalAnnotationCreationFormProps,
} from "./annotation-props";
import { ExternalAnnotationEditorHeader } from "./external-annotation-editor-header";
import {
  ExternalAnnotationError,
  isTokenExpirationError,
} from "./external-annotation-error";
import {
  getOptionValues,
  OptionEntry,
  TagWithParent,
} from "./external-annotation-ui-utils";
import { getExternalAnnotationProviderName } from "./external-annotation-utils";
import { HardcodedStatusIds, useHardcodedStatus } from "./use-hardcoded-status";

/** RFI annotations in ACC only support creating drafts. */
const ACC_HARDCODED_STATUS_IDS: HardcodedStatusIds = {
  [BcfServicesIntegrationType.autodeskAccRfis]: "draft",
};

type AccCompanionFieldsValues = {
  watchers?: TagWithParent[];
  rootCauses?: TagWithParent[];
  publishedDefaultValue?: boolean;
};

function getAccCompanionFieldsValues(
  companionFields: GetProjectExtensionsResponse["companion_fields"],
): AccCompanionFieldsValues {
  let watchers: TagWithParent[] | undefined = undefined;
  let rootCauses: TagWithParent[] | undefined = undefined;
  let publishedDefaultValue: boolean | undefined = undefined;

  for (const field of companionFields) {
    switch (field.id) {
      case AccCompanionFieldId.watchers:
        watchers = field.enumValues?.map((value) => ({
          id: value.value,
          name: value.displayValue,
          parentTagName: value.parentValue,
          disabled: value.readonly,
        }));
        break;
      case AccCompanionFieldId.rootCause:
        rootCauses = field.enumValues?.map((value) => ({
          id: value.value,
          name: value.displayValue,
          parentTagName: value.parentValue,
          disabled: value.readonly,
        }));
        break;
      case AccCompanionFieldId.published:
        publishedDefaultValue = field.defaultValue?.[0] === "true";
        break;
    }
  }
  return { watchers, rootCauses, publishedDefaultValue };
}

type AccAnnotationFormProps = Omit<
  CommonExternalAnnotationCreationFormProps,
  "stage" | "onStageChange"
> &
  AccCompanionFields & {
    /** Callback function called when the value of the root cause has changed */
    onRootCauseChange?(rootCause: string): void;

    /** Callback function called when the value of the start date has changed */
    onStartDateChange?(startDate: Date): void;

    /** Callback function called when the selected watchers have changed */
    onSelectedWatchersChange?(selectedWatchers: Tag[]): void;

    /** Callback function called when the value of the published field has changed */
    onPublishedChange?(published: boolean): void;
  };

/**
 * @returns A form containing all the fields required for the creation of ACC annotation.
 */
export function AccAnnotationForm({
  allowEdition,
  title,
  assignee,
  description = "",
  startDate,
  dueDate,
  status,
  topicType,
  priority,
  published,
  rootCause,
  selectedWatchers,
  externalAnnotationType,
  disabled,
  onTitleChange,
  onAssigneeChange,
  onDescriptionChange,
  onStatusChange,
  onSelectedWatchersChange,
  onRootCauseChange,
  onStartDateChange,
  onPriorityChange,
  onPublishedChange,
  onTopicTypeChange,
  onDueDateChange,
  onError,
  onConnectionTokenExpired,
  onConnectionTokenUpdated,
  showMandatoryFieldsError,
  setAreMandatoryFieldsFilled,
}: AccAnnotationFormProps): JSX.Element {
  const { bcfServicesApiClient } = useApiClientContext();
  const emptyProjectExtensions: GetProjectExtensionsResponse = useMemo(
    () => ({
      topic_type: [],
      topic_status: [],
      topic_label: [],
      priority: [],
      users: [],
      stage: [],
      project_actions: [],
      topic_actions: [],
      comment_actions: [],
      custom_fields: [],
      companion_fields: [],
    }),
    [],
  );

  const [projectExtensions, setProjectExtensions] =
    useState<GetProjectExtensionsResponse>(emptyProjectExtensions);
  const [currentUserInfo, setCurrentUserInfo] = useState<
    IntegrationUserInfo | undefined
  >();

  const [tokenExpirationError, setTokenExpirationError] = useState<
    BcfBackendApiErrorDetails | undefined
  >();
  const [titleErrorMessage, setTitleErrorMessage] = useState<
    string | undefined
  >();

  const [enableAssigneeError, setEnableAssigneeError] = useState(false);
  const [enablePriorityError, setEnablePriorityError] = useState(false);
  const [enableStatusError, setEnableStatusError] = useState(false);
  const [enableTypeError, setEnableTypeError] = useState(false);

  // Reset errors and title message when annotation type changes
  useEffect(() => {
    setEnableAssigneeError(false);
    setEnablePriorityError(false);
    setEnableStatusError(false);
    setEnableTypeError(false);
    setTitleErrorMessage(undefined);
  }, [externalAnnotationType]);

  const projectId = useAppSelector(
    selectProjectIdForIntegrationType(externalAnnotationType),
  );
  assert(projectId, "Project ID is required");

  useEffect(() => {
    // eslint-disable-next-line func-style -- FIXME
    const getProjectExtensions = async (): Promise<void> => {
      setProjectExtensions(emptyProjectExtensions);
      try {
        const extensions = await bcfServicesApiClient.getProjectExtensions(
          externalAnnotationType,
          projectId,
        );
        setProjectExtensions(extensions);
      } catch (error) {
        if (error instanceof ApiResponseError) {
          if (isBcfBackendApiErrorDetails(error.body)) {
            if (isTokenExpirationError(error.body)) {
              setTokenExpirationError(error.body);
              onConnectionTokenExpired?.();
            }
          }
        } else {
          onError?.({
            title: "Unable to get the project extensions",
            error,
          });
        }
      }
    };

    if (!tokenExpirationError) {
      getProjectExtensions();
    }
  }, [
    bcfServicesApiClient,
    emptyProjectExtensions,
    setProjectExtensions,
    externalAnnotationType,
    onError,
    setTokenExpirationError,
    onConnectionTokenExpired,
    projectId,
    tokenExpirationError,
  ]);

  const isRfiDraft =
    externalAnnotationType === BcfServicesIntegrationType.autodeskAccRfis;

  useEffect(() => {
    async function getCurrentUserInfo(): Promise<void> {
      try {
        const userInfo = await bcfServicesApiClient.getIntegrationUserInfo(
          externalAnnotationType,
        );
        setCurrentUserInfo(userInfo);
        // As the dropdown will be disabled, we are setting the assignee to the current user here
        onAssigneeChange(userInfo.id);
      } catch (error) {
        let errorMessage = error;
        if (error instanceof ApiResponseError) {
          if (isBcfBackendApiErrorDetails(error.body)) {
            errorMessage = (
              <ExternalAnnotationError
                integrationType={externalAnnotationType}
                errorDetails={error.body}
                onAuthorizationButtonClicked={() =>
                  Analytics.track<AuthorizeExternalAnnotationProperties>(
                    EventType.authorizeExternalAnnotation,
                    {
                      via: "error toast message",
                      service: getExternalAnnotationProviderName(
                        externalAnnotationType,
                      ),
                    },
                  )
                }
              />
            );
          }
        }
        onError?.({
          title: "Unable to get the current user info",
          error: errorMessage,
        });
      }
    }
    // The current user info is only required for RFI draft annotations
    if (isRfiDraft && !tokenExpirationError) {
      getCurrentUserInfo();
    }
  }, [
    bcfServicesApiClient,
    isRfiDraft,
    externalAnnotationType,
    onError,
    onAssigneeChange,
    tokenExpirationError,
  ]);

  // Create an option for the current user if available
  const currentUserOption: Option | undefined = useMemo(() => {
    if (currentUserInfo) {
      return {
        key: currentUserInfo.id,
        value: currentUserInfo.id,
        label: <NoTranslate>{currentUserInfo.name}</NoTranslate>,
      };
    }
  }, [currentUserInfo]);
  const assigneeOptions = useMemo(() => {
    if (isRfiDraft) {
      return currentUserOption ? [currentUserOption] : [];
    }
    return projectExtensions.users.map(
      (user): Option => ({
        key: user.id,
        value: user.id,
        label: <NoTranslate>{user.name ?? user.email}</NoTranslate>,
      }),
    );
  }, [isRfiDraft, currentUserOption, projectExtensions.users]);

  const statusOptions = useMemo(
    () =>
      projectExtensions.topic_status.map(
        (status): Option => ({
          key: status.id,
          value: status.id,
          label: <NoTranslate>{status.name}</NoTranslate>,
        }),
      ),
    [projectExtensions],
  );

  const topicTypeOptions = useMemo(() => {
    const data = projectExtensions.topic_type.map(
      (item) => new OptionEntry(item),
    );
    return getOptionValues(data);
  }, [projectExtensions]);

  const priorityOptions = useMemo(() => {
    const data = projectExtensions.priority.map(
      (item) => new OptionEntry(item),
    );
    return getOptionValues(data);
  }, [projectExtensions]);

  const accCompanionFieldsValues = useMemo(
    () => getAccCompanionFieldsValues(projectExtensions.companion_fields),
    [projectExtensions],
  );

  const rootCauseOptions = useMemo(() => {
    if (!accCompanionFieldsValues.rootCauses) {
      return [];
    }
    const data = accCompanionFieldsValues.rootCauses.map(
      (rootCause) => new OptionEntry(rootCause),
    );
    return getOptionValues(data);
  }, [accCompanionFieldsValues]);

  const selectedWatchersOptions = useMemo(
    () =>
      selectedWatchers?.map((watcher) => ({
        key: watcher.id,
        value: watcher.name,
        label: <NoTranslate>{watcher.name}</NoTranslate>,
      })),
    [selectedWatchers],
  );

  const watchersOptions = useMemo(
    () =>
      accCompanionFieldsValues.watchers?.map(
        (watcher): Option => ({
          key: watcher.id,
          value: watcher.name,
          label: <NoTranslate>{watcher.name}</NoTranslate>,
        }),
      ),
    [accCompanionFieldsValues],
  );

  const { handleErrorWithToast } = useErrorHandlers();

  // Reset errors and title message when annotation type changes
  useEffect(() => {
    const validTopicType =
      externalAnnotationType === BcfServicesIntegrationType.autodeskAccIssues
        ? !!topicType
        : true;
    const validPriority =
      externalAnnotationType === BcfServicesIntegrationType.autodeskAccRfis
        ? !!priority
        : true;

    setEnableAssigneeError(showMandatoryFieldsError && !assignee);
    setEnableStatusError(showMandatoryFieldsError && !status);
    setEnablePriorityError(showMandatoryFieldsError && !validPriority);
    setEnableTypeError(showMandatoryFieldsError && !validTopicType);

    setAreMandatoryFieldsFilled(
      !!status &&
        !titleErrorMessage &&
        !!assignee &&
        validPriority &&
        validTopicType,
    );
  }, [
    assignee,
    externalAnnotationType,
    priority,
    setAreMandatoryFieldsFilled,
    showMandatoryFieldsError,
    status,
    titleErrorMessage,
    topicType,
  ]);

  useEffect(() => {
    // There is no UI for setting the published value,
    // Here we are just setting the default value of the project
    if (
      !!accCompanionFieldsValues.publishedDefaultValue &&
      accCompanionFieldsValues.publishedDefaultValue !== published
    ) {
      onPublishedChange?.(accCompanionFieldsValues.publishedDefaultValue);
    }
  }, [accCompanionFieldsValues, onPublishedChange, published]);

  const isStatusHardcoded = useHardcodedStatus(
    externalAnnotationType,
    ACC_HARDCODED_STATUS_IDS,
    projectExtensions,
    onStatusChange,
  );

  // Callback to invoke when the token of the external annotation has been updated
  const onTokenUpdated = useCallback(() => {
    setTokenExpirationError(undefined);
    onConnectionTokenUpdated?.();
  }, [onConnectionTokenUpdated]);

  const translate = useTranslate();

  return (
    <>
      {tokenExpirationError && (
        <Alert
          variant="error"
          title="Connection Error"
          description={
            <ExternalAnnotationError
              integrationType={externalAnnotationType}
              errorDetails={tokenExpirationError}
              onTokenUpdated={onTokenUpdated}
              onAuthorizationButtonClicked={() =>
                Analytics.track<AuthorizeExternalAnnotationProperties>(
                  EventType.authorizeExternalAnnotation,
                  {
                    via: "creation dialog",
                    service: getExternalAnnotationProviderName(
                      externalAnnotationType,
                    ),
                  },
                )
              }
            />
          }
        />
      )}
      <ExternalAnnotationEditorHeader
        title={title}
        annotationType={externalAnnotationType}
        autoFocusTitle
        onTitleChange={onTitleChange}
        errorMessage={titleErrorMessage}
        onSetErrorMessage={setTitleErrorMessage}
        disabled={!allowEdition || !!disabled}
      />
      <Stack
        sx={{
          // Add a scrollbar only to the main content if needed
          overflow: "auto",
          flexGrow: 1,
          gap: 3,
          mx: -0.875,
          px: 0.875,
        }}
      >
        <FaroRichTextEditor
          initialText={description}
          dark
          fullWidth
          readOnly={!allowEdition || !!disabled}
          label="Description"
          placeholder={translate("Insert a description")}
          onError={(error) =>
            handleErrorWithToast({ title: "Error in description", error })
          }
          onChange={onDescriptionChange}
          sx={{
            height: "120px",
          }}
        />
        <Grid container {...legacyGridSpacing}>
          <Grid size={6}>
            <Dropdown
              label="Status"
              tag="star"
              disabled={
                !allowEdition ||
                statusOptions.length === 0 ||
                isStatusHardcoded ||
                !!disabled
              }
              value={status}
              options={statusOptions}
              dark
              fullWidth
              sx={dropDownStyle}
              onChange={(event) => onStatusChange?.(event.target.value)}
              error={enableStatusError}
              onClose={() => setEnableStatusError(!status)}
              shouldCapitalize={false}
            />
          </Grid>
          {externalAnnotationType ===
            BcfServicesIntegrationType.autodeskAccIssues && (
            <Grid size={6}>
              <Dropdown
                label="Type"
                tag="star"
                disabled={
                  !allowEdition || topicTypeOptions.length === 0 || !!disabled
                }
                value={topicType}
                options={topicTypeOptions}
                dark
                fullWidth
                sx={dropDownStyle}
                onChange={(event) => onTopicTypeChange?.(event.target.value)}
                error={enableTypeError}
                onClose={() => setEnableTypeError(!topicType)}
                shouldCapitalize={false}
              />
            </Grid>
          )}
          {externalAnnotationType ===
            BcfServicesIntegrationType.autodeskAccRfis && (
            <Grid size={6}>
              <Dropdown
                label="Priority"
                tag="star"
                disabled={
                  !allowEdition || priorityOptions.length === 0 || !!disabled
                }
                value={priority}
                options={priorityOptions}
                dark
                fullWidth
                sx={dropDownStyle}
                onChange={(event) => onPriorityChange?.(event.target.value)}
                error={enablePriorityError}
                onClose={() => setEnablePriorityError(!priority)}
                shouldCapitalize={false}
              />
            </Grid>
          )}
          <Grid size={6}>
            <SearchableDropdown
              selectedOption={assignee}
              options={assigneeOptions}
              label="Assignee"
              tag="star"
              onOptionChange={onAssigneeChange}
              error={enableAssigneeError}
              onClose={() => setEnableAssigneeError(!assignee)}
              disabled={
                !allowEdition ||
                assigneeOptions.length === 0 ||
                isRfiDraft ||
                !!disabled
              }
            />
          </Grid>
          {externalAnnotationType ===
            BcfServicesIntegrationType.autodeskAccIssues && (
            <Grid size={6}>
              <DropdownSelect
                dark
                disabled={
                  !allowEdition ||
                  watchersOptions === undefined ||
                  watchersOptions.length === 0 ||
                  !!disabled
                }
                label="Watchers"
                sx={dropDownStyle}
                renderValue={() => {
                  if (!selectedWatchers?.length) {
                    return (
                      <FaroText
                        variant="placeholder"
                        dark
                        sx={{
                          fontStyle: "italic",
                          mr: 1,
                          color: grey[500],
                        }}
                      >
                        Select watchers
                      </FaroText>
                    );
                  }

                  return (
                    <FaroChipTag
                      size="small"
                      label={`${selectedWatchers.length}`}
                      dark
                    />
                  );
                }}
              >
                <FilterMenuContent
                  dark
                  selectedOptions={selectedWatchersOptions ?? []}
                  options={watchersOptions ?? []}
                  onFilterChange={(options) => {
                    onSelectedWatchersChange?.(
                      options.map((o) => ({
                        id: o.key,
                        name: typeof o.label === "string" ? o.label : "",
                      })),
                    );
                  }}
                />
              </DropdownSelect>
            </Grid>
          )}
          {externalAnnotationType ===
            BcfServicesIntegrationType.autodeskAccIssues && (
            <Grid size={6} sx={legacyGridItemRowPadding}>
              <DatePicker
                label="Start date"
                disabled={!allowEdition || !!disabled}
                initialDate={startDate}
                variant="Dark"
                formSx={{ width: "100%" }}
                onChange={(date) => (date ? onStartDateChange?.(date) : {})}
                isClearable
              />
            </Grid>
          )}
          <Grid size={6} sx={legacyGridItemRowPadding}>
            <DatePicker
              label="Due date"
              disabled={!allowEdition || !!disabled}
              initialDate={dueDate}
              variant="Dark"
              formSx={{ width: "100%" }}
              onChange={(date) => (date ? onDueDateChange?.(date) : {})}
              isClearable
            />
          </Grid>
          {externalAnnotationType ===
            BcfServicesIntegrationType.autodeskAccIssues && (
            <Grid size={6}>
              <Dropdown
                label="Root cause"
                disabled={
                  !allowEdition || rootCauseOptions.length === 0 || !!disabled
                }
                value={rootCause}
                options={rootCauseOptions}
                dark
                fullWidth
                sx={dropDownStyle}
                onChange={(event) => onRootCauseChange?.(event.target.value)}
                shouldCapitalize={false}
              />
            </Grid>
          )}
        </Grid>
      </Stack>
    </>
  );
}
